import { Hub } from "@aws-amplify/core";
import * as React from "react";

import { PCLogger } from "../server/ServerContext";
import Auth, { isUnauthenticated } from "./Auth";

export interface AuthProviderProps {
  config: any;
}

export interface AuthContextValue {
  authState: AuthState;
  initializeAuth: () => void;
  setAuthState: (authState: AuthState) => any;
}

export enum AuthState {
  IDLE = "idle",
  INITIALIZING = "initializing",
  AUTHENTICATED = "authenticated",
  UNAUTHENTICATED = "unauthenticated",
  UNKNOWN = "unknown",
}

export const AuthContext = React.createContext<AuthContextValue | undefined>(
  undefined
);

export const AuthProvider: React.FunctionComponent<AuthProviderProps> = ({
  children,
  config,
}) => {
  const [authState, setAuthState] = React.useState<AuthState>(AuthState.IDLE);

  const initializeAuth = async () => {
    try {
      await Auth.currentAuthenticatedUser();
      setAuthState(AuthState.AUTHENTICATED);
    } catch (error) {
      if (isUnauthenticated(error)) {
        setAuthState(AuthState.UNAUTHENTICATED);
      } else {
        setAuthState(AuthState.UNKNOWN);
      }
    }
  };

  React.useEffect(() => {
    if (authState === AuthState.IDLE) {
      Auth.configure(config);
    }
  }, [config, authState]);

  Hub.listen("auth", ({ payload: { event } }) => {
    switch (event) {
      /**
       * The Hub "signIn" event is the only way to fully resolve
       *  an Auth.federatedSignIn
       */
      case "signIn":
        setAuthState(AuthState.INITIALIZING);
        return;
      case "signIn_failure":
      case "tokenRefresh_failure":
      case "autoSignIn_failure":
        PCLogger.info(`[AuthContext] Hub Event: ${event}`);
        return;
    }
  });

  return (
    <AuthContext.Provider value={{ authState, initializeAuth, setAuthState }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = React.useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within AuthContext");
  }

  return context;
};

export const useAuthState = () => {
  const context = useAuthContext();

  return {
    authState: context.authState,
    setAuthState: context.setAuthState,
  };
};

export default AuthContext;
